import { makeObservable, observable, runInAction } from "mobx";
import { RootStore } from "./RootStore";
import { TransportService } from "../services/TransportService";
import DataStore from "./DataStore";
import { Field } from "../models/Fields";
import { FieldGroup } from "../models/FieldGroups";
import { FieldId } from "../lib/enums";
import {
  convertFieldToFieldOptionsList,
  convertFieldToMappedFieldOptions,
  convertSymptomCodesToSymptomNameMap,
} from "../utils/fieldHelpers";
import { Option } from "../components/customform/types";
import { symptomCodes } from "../models/SymptomDiary";

class FieldStore extends DataStore {
  rootStore: RootStore;
  fieldGroups: FieldGroup[] | undefined = undefined;
  fields: Field[] | undefined = undefined;
  mappedSideEffects: Map<number, string> | undefined = undefined;
  mappedDrugs: Map<number, string> | undefined = undefined;
  mappedVaccines: Map<number, string> | undefined = undefined;
  vaccinesOptionsList: Option[] = [];
  mappedSymptomNames: Map<string, string> | undefined = undefined;

  constructor(transportService: TransportService, rootStore: RootStore) {
    super(transportService);
    this.rootStore = rootStore;
    makeObservable(this, {
      fields: observable,
      fieldGroups: observable,
      mappedSideEffects: observable,
      mappedDrugs: observable,
      mappedVaccines: observable,
      mappedSymptomNames: observable,
      vaccinesOptionsList: observable,
      success: observable,
      error: observable,
    });
  }

  setFieldGroups(fieldGroups: FieldGroup[] | undefined) {
    runInAction(() => {
      this.fieldGroups = fieldGroups ?? [];
    });
  }

  setFields(fields: Field[] | undefined) {
    const sideEffectsField = fields?.find(
      (f) => f.fieldId === FieldId.BIVERKNINGAR_FIELD_ID
    );
    const drugsField = fields?.find(
      (f) => f.fieldId === FieldId.DRUGS_FIELD_ID
    );
    const vaccinationsField = fields?.find(
      (f) => f.fieldId === FieldId.VACCINATIONS_FIELD_ID
    );
    const symptomsFields =
      fields?.filter((f) => symptomCodes.includes(f.alias)) || [];
    const symptomNames = convertSymptomCodesToSymptomNameMap(symptomsFields);
    const newSideEffects = convertFieldToMappedFieldOptions(sideEffectsField);
    const newDrugs = convertFieldToMappedFieldOptions(drugsField);
    const newVaccines = convertFieldToMappedFieldOptions(vaccinationsField);
    const vaccinesOptionsList = convertFieldToFieldOptionsList(
      vaccinationsField,
      "name",
      "asc"
    );

    runInAction(() => {
      this.fields = fields ?? [];
      this.mappedSideEffects = newSideEffects;
      this.mappedDrugs = newDrugs;
      this.mappedVaccines = newVaccines;
      this.vaccinesOptionsList = vaccinesOptionsList;
      this.mappedSymptomNames = symptomNames;
    });
  }

  getFields = async () => {
    const token = await this.rootStore.keycloakStore.getRefreshedToken();
    if (!token) {
      return;
    }
    try {
      this.clearError("getFields");
      this.setLoading("getFields");
      const res = await this.transportService.getWithToken("/fields", token);
      this.setFields(res.data.fields);
    } catch (e) {
      console.error(e);
      this.setError("getFields");
      return;
    } finally {
      this.clearLoading("getFields");
    }
  };

  getField = (id: number) => {
    if (!this.fields) {
      return null;
    }
    return this.fields.find((f) => f.fieldId === id);
  };

  getFieldGroups = async () => {
    const token = await this.rootStore.keycloakStore.getRefreshedToken();
    if (!token) {
      return;
    }
    try {
      this.clearError("getFieldGroups");
      this.setLoading("getFieldGroups");
      const res = await this.transportService.getWithToken(
        "/fieldgroups",
        token
      );
      this.setFieldGroups(res.data.fieldGroups);
    } catch (e) {
      console.error(e);
      this.setError("getFieldGroups");
      return;
    } finally {
      this.clearLoading("getFieldGroups");
    }
  };

  getFieldGroup = async (id: number) => {
    return this.fieldGroups?.find((a) => a.id === id);
  };
}

export default FieldStore;
